import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';

const { FiClock, FiPlay, FiSquare, FiChevronDown, FiBriefcase, FiCheckSquare } = FiIcons;

const GlobalTimer = () => {
  const { projects, tasks, addTimeEntry } = useData();
  const [active, setActive] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [startTime, setStartTime] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    const saved = localStorage.getItem('jinxos_active_timer');
    if (saved) {
      try {
        const { start, projectId, taskId, isActive } = JSON.parse(saved);
        if (isActive) {
          const startTimestamp = new Date(start).getTime();
          const elapsed = Math.floor((Date.now() - startTimestamp) / 1000);
          setSeconds(elapsed > 0 ? elapsed : 0);
          setStartTime(new Date(start));
          setSelectedProjectId(projectId);
          setSelectedTaskId(taskId);
          setActive(true);
        }
      } catch (e) {
        localStorage.removeItem('jinxos_active_timer');
      }
    }
  }, []);

  useEffect(() => {
    if (active && startTime) {
      intervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - new Date(startTime).getTime()) / 1000);
        setSeconds(elapsed > 0 ? elapsed : 0);
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [active, startTime]);

  const handleToggle = async () => {
    if (!active) {
      const now = new Date();
      setActive(true);
      setSeconds(0);
      setStartTime(now);
      localStorage.setItem('jinxos_active_timer', JSON.stringify({
        start: now.toISOString(),
        projectId: selectedProjectId,
        taskId: selectedTaskId,
        isActive: true
      }));
    } else {
      setActive(false);
      const now = new Date();
      
      const taskObj = tasks.find(t => t.id === selectedTaskId);
      const projectObj = projects.find(p => p.id === selectedProjectId);

      await addTimeEntry({
        description: `Timer: ${taskObj?.title || 'General Work'}`,
        projectId: selectedProjectId || null,
        taskId: selectedTaskId || null,
        duration: seconds,
        date: now.toISOString().split('T')[0],
        startTime: startTime?.toISOString(),
        endTime: now.toISOString()
      });

      setSeconds(0);
      setStartTime(null);
      localStorage.removeItem('jinxos_active_timer');
    }
  };

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  const filteredTasks = tasks.filter(t => t.projectId === selectedProjectId);

  return (
    <div className="relative flex items-center gap-2">
      <div className={`flex items-center gap-4 px-8 py-4 rounded-[2.5rem] transition-all shadow-sm ${active ? 'bg-red-50 text-red-600 ring-4 ring-red-100 border-red-200' : 'bg-slate-50 text-slate-600 border border-slate-100 hover:border-slate-300'}`}>
        <div className="flex items-center gap-3 font-mono font-black text-xl">
          <SafeIcon icon={FiClock} className={active ? 'animate-pulse text-red-500' : ''} />
          {formatTime(seconds)}
        </div>
        <div className="h-8 w-[1px] bg-slate-200" />
        <button 
          onClick={() => setShowDropdown(!showDropdown)} 
          className="flex items-center gap-2 text-xs font-black uppercase tracking-widest hover:text-blue-600 transition-colors px-2"
        >
          {selectedProjectId ? projects.find(p => p.id === selectedProjectId)?.name : 'Select Client'}
          <SafeIcon icon={FiChevronDown} className={`transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </button>
        <button 
          onClick={handleToggle} 
          className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${active ? 'bg-red-600 text-white shadow-2xl shadow-red-200' : 'bg-white text-slate-900 border border-slate-200 shadow-sm hover:border-blue-500'}`}
        >
          <SafeIcon icon={active ? FiSquare : FiPlay} className="text-xl" />
        </button>
      </div>

      <AnimatePresence>
        {showDropdown && (
          <motion.div 
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="absolute top-full right-0 mt-6 w-[500px] bg-white rounded-[4rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.15)] border border-slate-100 p-12 z-[100] space-y-10"
          >
            <div className="space-y-4">
              <label className="text-[12px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-3 pl-2">
                <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                  <SafeIcon icon={FiBriefcase} />
                </div>
                Active Project
              </label>
              <select 
                className="w-full p-6 bg-slate-50 rounded-3xl border-none font-black text-sm outline-none focus:ring-4 focus:ring-blue-100 appearance-none cursor-pointer hover:bg-slate-100 transition-all"
                value={selectedProjectId}
                onChange={e => {
                  setSelectedProjectId(e.target.value);
                  setSelectedTaskId('');
                }}
              >
                <option value="">No Project Linked</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>

            <div className="space-y-4">
              <label className="text-[12px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-3 pl-2">
                <div className="w-8 h-8 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600">
                  <SafeIcon icon={FiCheckSquare} />
                </div>
                Target Task
              </label>
              <select 
                disabled={!selectedProjectId}
                className="w-full p-6 bg-slate-50 rounded-3xl border-none font-black text-sm outline-none focus:ring-4 focus:ring-blue-100 appearance-none disabled:opacity-30 cursor-pointer hover:bg-slate-100 transition-all"
                value={selectedTaskId}
                onChange={e => setSelectedTaskId(e.target.value)}
              >
                <option value="">No Task Linked</option>
                {filteredTasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
            </div>

            <button 
              onClick={() => setShowDropdown(false)}
              className="w-full bg-slate-900 text-white py-7 rounded-[2.5rem] font-black text-sm hover:bg-black transition-all shadow-2xl shadow-slate-200 uppercase tracking-widest"
            >
              Set Tracking Context
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GlobalTimer;